import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AccessDenied from "../../components/Reusable/AccessDenied";
import ConfirmationPopup from "../../components/Reusable/ConfirmationPopup";

import loadingGif from "../../assets/images/loading-loading-forever.gif";

import DataSubmittedPopup from "../../utils/Popups/DataSubmittedPopup";
import UploadEmployeeManagementData from "./UploadEmployeeManagementData";

import LeaveLogPopup from "./LeaveLogPopup";
import useEmployeeStore from "../../store/useEmployeeStore";

import { filterEmployees } from "../../utils/employeeFilters";

import { usePagination } from "../../hooks/usePagination";
import { useEmployeeSearch } from "../../hooks/useEmployeeSearch";

import EmployeeTableActions from "./components/EmployeeTableActions";

import EmployeePagination from "./components/EmployeePagination";
import { downloadEmployeeData } from "./utils/employeeDownload";
import EmployeeTable from "./components/EmployeeTable";

const EmployeeManagementTable = (props) => {
  const permissions = JSON.parse(localStorage.getItem("userPermissions"));
  const accessToken = localStorage.getItem("accessToken");
  const userData = localStorage.getItem("userData");
  const { selectedCompany, selectedCompanyId, selectedFilter, searchQuery } =
    props;

  const { fetchEmployees } = useEmployeeStore();

  const [employeeData, setEmployeeData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [selectedCheckboxId, setSelectedCheckboxId] = useState(null);
  const [showAccessDeniedPopup, setShowAccessDeniedPopup] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAccessPage403, setShowAccessPage403] = useState(false);
  const [employeeInformationId, setEmployeeInformationId] = useState(null);
  const [sortOrder, setSortOrder] = useState({});
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [message, setMessage] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [showLeaveLog, setShowLeaveLog] = useState(false);
  const ITEMS_PER_PAGE = 10;
  const navigate = useNavigate();

  const searchedEmployees = useEmployeeSearch(employeeData, searchQuery);
  const paginatedData = usePagination(
    searchedEmployees,
    currentPage,
    ITEMS_PER_PAGE,
  );
  useEffect(() => {
    const userDataFilter = JSON.parse(userData);
    const empInfoId = userDataFilter.user[0].employee_information_id;
    setEmployeeInformationId(empInfoId);
  }, [userData]);

  const hasViewPerGroupPermission = permissions.some(
    (permission) => permission.name === "view_per_group_all_employees_info",
  );
  const hasViewAllManagedPermission = permissions.some(
    (permission) => permission.name === "view_all_managed_employees_info",
  );

  const getEmployeeData = async (preservePage = false) => {
    try {
      const employees = await fetchEmployees({
        selectedCompany,
        permissions,
      });

      const filteredAndSortedEmployees = filterEmployees(
        employees,
        selectedFilter,
      );

      setEmployeeData(filteredAndSortedEmployees);

      if (preservePage) {
        const totalPages = Math.ceil(
          filteredAndSortedEmployees.length / ITEMS_PER_PAGE,
        );

        if (currentPage > totalPages && totalPages > 0) {
          setCurrentPage(totalPages);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getEmployeeData();
    setCurrentPage(1);
  }, [selectedCompany, selectedFilter, searchQuery]);

  useEffect(() => {
    if (searchQuery) {
      const foundIndex = searchedEmployees.findIndex(
        (user) =>
          user.employee_full_name
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          user.employee_id?.toLowerCase().includes(searchQuery.toLowerCase()),
      );

      if (foundIndex !== -1) {
        const page = Math.ceil((foundIndex + 1) / ITEMS_PER_PAGE);

        setCurrentPage(page);
      }
    }
  }, [searchQuery, searchedEmployees, ITEMS_PER_PAGE]);

  const handleCheckboxChange = (employeeId) => {
    if (selectedCheckboxId === employeeId) {
      setSelectedCheckboxId(null);
      setSelectedEmployeeIds([]);
    } else {
      setSelectedCheckboxId(employeeId);
      setSelectedEmployeeIds([employeeId]);
    }
  };

  const handleEdit = () => {
    if (selectedEmployeeIds.length === 0) {
      alert("Choose any employee");
    } else {
      const hasEditPermission = permissions.some(
        (permission) => permission.name === "update_employee_info",
      );

      if (!hasEditPermission) {
        setShowAccessDeniedPopup(true);
        return;
      }

      const idToNavigate = selectedEmployeeIds[0];
      const newPath = `/employeedirectory/${idToNavigate}`;
      navigate(newPath, { replace: true });
    }
  };

  const handleView = () => {
    if (selectedEmployeeIds.length === 0) {
      alert("Choose any employee");
    }

    const idToNavigate = selectedEmployeeIds[0];
    const newPath = `/viewdirectory/${idToNavigate}`;
    navigate(
      newPath,

      { replace: true },
    );
  };

  const moveToEmployeeDetails = (id) => {
    const hasEditPermission = permissions.some(
      (permission) => permission.name === "update_employee_info",
    );

    if (!hasEditPermission) {
      setShowAccessDeniedPopup(true);
      return;
    }

    const newPath = `/viewdirectory/${id}`;
    navigate(newPath, { replace: true });
  };

  const handleDelete = async () => {
    if (selectedEmployeeIds.length === 0) {
      alert("Choose any employee");
      return;
    }

    const hasDeletePermission = permissions.some(
      (permission) => permission.name === "delete_employee_info",
    );

    if (!hasDeletePermission) {
      alert("You don't have permission to delete employees");
      return;
    }

    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      let deactivatedEmployeeName = "";

      // Close the delete confirmation popup first
      setShowDeleteConfirmation(false);

      for (const id of selectedEmployeeIds) {
        const employeeToBeDeleted = employeeData.find(
          (employee) => employee.employee_information_id === id,
        );

        if (employeeToBeDeleted && employeeToBeDeleted.is_active === 0) {
          deactivatedEmployeeName = employeeToBeDeleted.employee_full_name;

          // Show the message popup and return early
          setMessage(
            `Employee ${deactivatedEmployeeName} is already deactivated`,
          );
          setConfirmationPopup(true);
          return; // Exit early to avoid processing other employees
        }

        await axios.patch(
          `${process.env.REACT_APP_BASE_URL}/employees/employeeInformation/${id}`,
          { is_active: 0 },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
      }

      // Pass true to preserve the current page
      await getEmployeeData(true);
      setSelectedEmployeeIds([]);
      setSelectedCheckboxId(null);

      // Show success message
      setMessage("Selected employees deactivated successfully!");
      setConfirmationPopup(true);
    } catch (error) {
      console.log(error);
      setShowDeleteConfirmation(false);

      // Optionally show an error message
      setMessage(
        "An error occurred while deactivating employees. Please try again.",
      );
      setConfirmationPopup(true);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Download the data in excel format
  const handleDownload = async () => {
    try {
      await downloadEmployeeData({
        selectedCompanyId,
        employeeInformationId,
        hasViewPerGroupPermission,
        hasViewAllManagedPermission,
        accessToken,
      });
    } catch (error) {
      if (error.message === "NO_PERMISSION") {
        setShowAccessPage403(true);
        return;
      }

      console.error(error);
    }
  };
  // Get paginated data

  const goToPreviousPage = () => {
    setCurrentPage((prevPage) => prevPage - 1);
  };

  const goToNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handleSort = (columnName) => {
    const currentOrder = sortOrder[columnName];

    const newOrder = currentOrder === "asc" ? "desc" : "asc";
    setSortOrder({ [columnName]: newOrder });
    const sortedData = [...employeeData].sort((a, b) => {
      if (columnName === "is_active") {
        if (newOrder === "asc") {
          return a[columnName] - b[columnName];
        } else {
          return b[columnName] - a[columnName];
        }
      } else {
        if (newOrder === "asc") {
          return a[columnName].localeCompare(b[columnName]);
        } else {
          return b[columnName].localeCompare(a[columnName]);
        }
      }
    });
    setEmployeeData(sortedData);
  };

  const handleShowLeaveLogButton = (id) => {
    setActiveId((prev) => (prev === id ? null : id));
  };

  const handleShowLeaveLog = (id) => {
    setShowLeaveLog(true);
    setActiveId(null);
  };

  console.log("Employee Data", employeeData)

  return (
    <div>
      <EmployeeTableActions
        setShowUploadPopup={setShowUploadPopup}
        handleDownload={handleDownload}
        handleView={handleView}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
      />
      {showDeleteConfirmation && (
        <ConfirmationPopup
          confirm={confirmDelete}
          cancel={cancelDelete}
          title="Delete the Employee"
          question="Are you sure that you want to delete the employee?"
        />
      )}

      {/* Table Area */}
      <EmployeeTable
        employeeData={employeeData}
        paginatedData={paginatedData}
        sortOrder={sortOrder}
        handleSort={handleSort}
        selectedEmployeeIds={selectedEmployeeIds}
        selectedCheckboxId={selectedCheckboxId}
        activeId={activeId}
        handleCheckboxChange={handleCheckboxChange}
        moveToEmployeeDetails={moveToEmployeeDetails}
        handleShowLeaveLogButton={handleShowLeaveLogButton}
        handleShowLeaveLog={handleShowLeaveLog}
        setEmployeeInformationId={setEmployeeInformationId}
        loadingGif={loadingGif}
      />

      {showAccessDeniedPopup && (
        <div className="em_popup-overlay">
          <div className="em_popup-content">
            <AccessDenied setShowAccessDeniedPopup={setShowAccessDeniedPopup} />
          </div>
        </div>
      )}

      {searchQuery ? null : (
        <EmployeePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          employeeData={employeeData}
          ITEMS_PER_PAGE={ITEMS_PER_PAGE}
          searchQuery={searchQuery}
          goToPreviousPage={goToPreviousPage}
          goToNextPage={goToNextPage}
        />
      )}
      {confirmationPopup && (
        <DataSubmittedPopup
          message={message}
          setConfirmationPopup={setConfirmationPopup}
          updateTable={getEmployeeData}
        />
      )}
      {showUploadPopup && (
        <UploadEmployeeManagementData setShowUploadPopup={setShowUploadPopup} />
      )}
      {showLeaveLog && (
        <LeaveLogPopup
          setShowLeaveLog={setShowLeaveLog}
          user={employeeInformationId}
        />
      )}
    </div>
  );
};

export default EmployeeManagementTable;
